import React from 'react';
import { NodonListItemResult } from '@ui/ListItemResult';
import { getElementPropertyResolvedCountByNameOrId } from '@shared/helpers/element-property.helpers';
import {
  getElementQuantityResolvedCountByName,
  getSelectedQuantityNameByCategoryId,
} from '@shared/helpers/element-quantity.helpers';
import { ElementPropertyName } from '@shared/models/element-property.model';
import {
  useAverageThermalTransmittance,
  useBuildingEnvelopeElements,
} from '@features/energy/energy.hook';
import { THERMAL_BRIDGES_LOSS_FACTOR } from '@shared/helpers/energy/energy.models';
import { toPercent } from '@shared/helpers/string.helpers';
import { getElementName } from '@shared/helpers/element.helpers';
import { IElement } from '@shared/models/project.model';
import { guards } from '@shared/helpers/typeguards.helpers';
import { getDisplayValueData } from '@shared/helpers/display/get-display-value-data.helper';

export const EnvelopeTransmittanceGroup: React.FC<{
  indentation?: number;
}> = ({ indentation = 0 }) => {
  const buildingEnvelopeElements = useBuildingEnvelopeElements().filter(
    (element) =>
      getElementPropertyResolvedCountByNameOrId(
        element,
        ElementPropertyName.ThermalTransmittance,
        0,
      ) > 0, // Support the window hack (with uValue on parent wall element) and don't include windows that have no thermal transmittance
  );
  const averageThermalTransmittance = useAverageThermalTransmittance();

  const getElementArea = (element: IElement) => {
    const area = getElementQuantityResolvedCountByName(
      element,
      getSelectedQuantityNameByCategoryId(element) ?? 'area_side',
    );
    return guards.is.number(area) ? area : 0;
  };

  return (
    <NodonListItemResult
      name="Envelope transmittance (uₘ)"
      tooltip={[
        `The average thermal transmittance of the building, how much heat is lost per square meter and per degree temperature difference.`,
        `Includes a factor of ${toPercent(THERMAL_BRIDGES_LOSS_FACTOR)}`,
      ].join(' ')}
      value={averageThermalTransmittance}
      unit="W/(m^2*K)" // Should be displayed as W/m^2K, no automatic conversion to kW/m^2
      indentation={indentation}
    >
      {/* Building envelope elements */}
      {buildingEnvelopeElements.map((element) => (
        <NodonListItemResult
          key={element.id}
          name={`${getElementName(element)} (${getDisplayValueData({ value: getElementArea(element), unit: 'm^2' }).full})`}
          tooltip={`The thermal transmittance of "${element.name}" excluding thermal bridges loss factor.`}
          value={getElementPropertyResolvedCountByNameOrId(
            element,
            ElementPropertyName.ThermalTransmittance,
          )}
          unit="W/(m^2*K)"
          indentation={indentation + 2}
        />
      ))}

      {/* Thermal bridges loss factor */}
      <NodonListItemResult
        name="Thermal bridges loss factor"
        tooltip="The thermal bridges loss factor is a factor that is used to account for the additional heat loss due to thermal bridges in the building envelope."
        value={THERMAL_BRIDGES_LOSS_FACTOR}
        unit="%"
        indentation={indentation + 1}
      />
    </NodonListItemResult>
  );
};
